<template>
  <div class="prq-section py-2 pl-0">
    <div class="content-box content-box-plus child row pt-4">
      <div class="col-12" v-if="!queryHidePRQTitle || !queryHidePRQMessage">
        <header>
          <div class="title" v-if="!queryHidePRQTitle">
            {{ section.name }}
          </div>
          <p class="description-large" v-if="!queryHidePRQMessage" v-safe-html="section.message_or_instructions" />
        </header>
      </div>
      <template>
        <div class="container form pb-4">
          <div class="header sub-title row pt-2" v-if="!queryHideQuestionLabel">
            <div class="col-sm">
              Question or Requirement
            </div>
            <div class="col-sm">
              Response
            </div>
          </div>
          <div v-for="question in section.questions" :key="question.id" v-if="checkVisibility(question)">
            <div class="row form pt-3">
              <div class="col-sm m-auto align-self-center response-title field-label">
                <div class="d-flex align-items-start">
                  <div class="v-safe-html" :class="addFlexGrowClass(question)">
                    <span v-if="hasQuestionRichtext(question)" v-safe-html="getQuestionRichText(question)"></span>
                    <span v-else>{{ question.question }}</span>
                  </div>
                  <div class="text-danger ps-1" v-if="isQuestionsRequired(question) && !isQuestionsReadOnly(question)">*</div>
                </div>
              </div>
              <div class="col-sm align-self-center" @focusout="emitAnswerList()" v-if="!isQuestionsReadOnly(question)">
                <validation-provider
                  :rules="addValidation(question)"
                  v-slot="{ errors }"
                  :ref="'ql-item-' + question.id"
                  :custom-messages="customErrorMsg"
                >
                  <toggle-button
                    v-model="responses[`${question.id}`]['response']"
                    class="question-list-item"
                    :name="question.name"
                    :id="question.id"
                    :width="62"
                    :height="25"
                    :font-size="15"
                    :labels="{ checked: 'Yes', unchecked: 'No' }"
                    v-if="question.type === 'Boolean'"
                  />

                  <b-input-group v-else-if="question.type.toLowerCase() === 'date'">
                    <b-form-input
                      :id="`${question.id}`"
                      v-model="responses[`${question.id}`]['response']"
                      :name="question.name"
                      type="text"
                      class="question-list-item d-none"
                      :class="{ 'box-alert': errors[0] }"
                    />
                    <b-input-group-append class="w-100">
                      <b-form-datepicker 
                        class="input-text" 
                        :class="{ 'box-alert': errors[0] }"
                        today-button 
                        reset-button 
                        close-button 
                        v-model="responses[`${question.id}`]['response']" 
                      />
                    </b-input-group-append>
                  </b-input-group>

                  <div v-else-if="question.type === 'multi_select'">
                    <multiselect
                      v-model="responses[`${question.id}`]['response']"
                      tag-placeholder="Add this as new option"
                      placeholder="Select all that apply."
                      :options="getOptions(question.rules)"
                      :multiple="true"
                      :taggable="true"
                      :class="{ 'box-alert': errors[0] }"
                    />
                    <input
                      type="text"
                      :name="question.name"
                      v-model="responses[`${question.id}`]['response']"
                      :id="question.id"
                      class="question-list-item form-control multi-select d-none field input-text"
                      :class="{ 'box-alert': errors[0] }"
                    />
                  </div>
                  <div v-else-if="['Specific values', 'Specific values (long list)'].includes(question.type)">
                    <multiselect
                      v-model="responses[`${question.id}`]['response']"
                      :options="getOptions(question.rules)"
                      :placeholder="question.required === 'true' ? getPlaceHolder(question.rules) : 'None'"
                      :multiple="false"
                      :taggable="false"
                      :class="{ 'box-alert': errors[0] }"
                    />
                    <input
                      type="text"
                      :name="question.name"
                      v-model="responses[`${question.id}`]['response']"
                      :id="question.id"
                      class="question-list-item form-control single-select d-none field input-text"
                      :class="{ 'box-alert': errors[0] }"
                    />
                  </div>
                  <div v-else-if="question.type === 'Specific values (multi-select)'">
                    <multiselect
                      v-model="multiSelectResponses[question.id]"
                      :options="getOptions(question.rules)"
                      :placeholder="question.required === 'true' ? getPlaceHolder(question.rules) : 'None'"
                      :multiple="true"
                      :taggable="false"
                      @input="updateMultiSelectResponse(question.id)"
                      class="specific-values-multi-select"
                      :class="{ 'box-alert': errors[0] }"
                    />
                    <input
                      type="text"
                      :name="question.name"
                      :value="responses[`${question.id}`]['response']"
                      :id="question.id"
                      class="question-list-item form-control multi-select d-none field input-text"
                      :class="{ 'box-alert': errors[0] }"
                    />
                  </div>
                  <input
                    type="number"
                    :name="question.name"
                    v-model="responses[`${question.id}`]['response']"
                    :id="question.id"
                    class="question-list-item form-control field input-text"
                    :class="{ 'box-alert': errors[0] }"
                    v-else-if="question.type === 'Numeric'"
                    @keydown="blockInvalidChars"
                  />
                  <textarea
                    :name="question.name"
                    v-model="responses[`${question.id}`]['response']"
                    :id="question.id"
                    class="question-list-item form-control field input-text"
                    :class="{ 'box-alert': errors[0] }"
                    v-else-if="question.type === 'Free-form'"
                  />
                  <input
                    type="text"
                    :name="question.name"
                    v-model="responses[`${question.id}`]['response']"
                    :id="question.id"
                    class="question-list-item form-control field input-text"
                    :class="{ 'box-alert': errors[0] }"
                    v-else-if="question.type === 'Free-form (single line)'"
                  />

                  <span class="error-message" v-if="errors[0]" :class="errors[0] ? 'has-error' : ''">{{ errMsg = errors[0] }}</span>
                </validation-provider>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import FieldHelper from '@/shared/mixins/field-helper';
import DOMPurify from "dompurify";

export default {
  name: 'Prqs',

  components: {
    Multiselect,
  },

  mixins: [ FieldHelper ],

  props: {
    section: Object,
    isRegistration: Boolean,
    hideQuestionLabel: Boolean,
  },

  data() {
    return {
      responses: {},
      responses2: {},
      originalResponse: {},
      rules: {},
      customErrorMsg: {},
      errMsg: '',
    };
  },

  watch: {
    responses: {
      handler(newVal) {
        for (const [key, value] of Object.entries(newVal)) {
          this.responses2[value.external_id] = value.response;
        }
      },
      deep: true,
    }
  },

  created() {
    this.section.questions.forEach((question) => {
      this.setResponse(question, question.response);

      this.originalResponse[question.id] = question.response;

      if (question.external_id) { this.$set(this.responses2, question.external_id, question.response); }

      if (question.visibility_rules && question.visibility_rules.length > 0) {
        const parents = question.visibility_rules.split(';');
        this.rules[question.external_id] = {}
        parents.forEach((parent) => {
          const parentValueSplit = parent.split('=');
          this.rules[question.external_id][parentValueSplit[0]] = parentValueSplit[1].split('|');
        });
      }
    });

    this.section.questions =
      this.section.questions.sort(
        (a, b) => a.sorting_tag.localeCompare(b.sorting_tag, 'en', { numeric: true })
      );
  },

  computed: {
    multiSelectResponses() {
      const result = {};
      Object.keys(this.responses).forEach((questionId) => {
        const response = this.responses[questionId]?.response;
        if (response && typeof response === 'string') {
          result[questionId] = response.split(';').map(item => item.trim());
        } else {
          result[questionId] = Array.isArray(response) ? response : [];
        }
      });
      return result;
    },

    queryHidePRQTitle() {
      return this.$route.query.hidePRQTitle === 'true';
    },

    queryHidePRQMessage() {
      return this.$route.query.hidePRQMessage === 'true';
    },

    queryHideQuestionLabel() {
      return this.$route.query.hideQuestionLabel === 'true' || this.hideQuestionLabel;
    },
  },

  methods: {
    addValidation(question) {
      const validationList = [];

      if (question.required === 'true') {
        this.customErrorMsg['required'] = "This field is required.";
        validationList.push('required');
      }

      if (question.type === 'Numeric') {
        this.customErrorMsg['numeric'] = "This field only accepts numeric values.";
        validationList.push('numeric');
      } else if (question.type === 'Float') {
        this.customErrorMsg['decimal'] = "This field only accepts decimal values.";
        validationList.push('decimal');
      }

      return validationList.join('|');
    },

    emitAnswerList() {
      this.$emit('answerList', this.section.id, this.responses);
    },

    getOptions(rules) {
      let options = rules.replace(/(\r\n|\n|\r)/gm, '').split(';');
      options = options.filter((option) => {
        return option.charAt(0) !== '-' && option.charAt(option.length-1) !== '-' && option !== '';
      });

      return options;
    },

    getPlaceHolder(rules) {
      const placeHolder = rules.split(';').filter((option) => {
        return option.charAt(0) === '-' && option.charAt(option.length-1) === '-';
      });

      let placeHolderValue = 'Required';

      if (placeHolder.length > 0 && placeHolder[0]) {
        placeHolderValue = placeHolder[0].substring(1, placeHolder[0].length-1).trim();
      }

      return placeHolderValue;
    },

    updateMultiSelectResponse(questionId) {
      const model = this.multiSelectResponses[questionId];
      this.responses[questionId].response = model.join(';');
    },

    setResponse(question, response) {
      let responseDetails = {
        id: question.id,
        type: question.type,
        response: response,
        external_id: question.external_id
      }

      if (this.isRegistration) {
        responseDetails.cross_program_id = question.cross_program_id;
        responseDetails.program_id = question.program_id;
      }

      this.$set(this.responses, question.id, responseDetails);
    },

    checkVisibility(question) {
      const externalId = question.external_id;
      if (!externalId || !this.rules[externalId]) { return true; }

      for (const [key, value] of Object.entries(this.rules[externalId])) {
        if (value.includes(this.responses2[key])) { return true; }
      }

      this.setResponse(question, this.originalResponse[question.id]);

      return false;
    },

    isQuestionsReadOnly(question) {
      return question.type.toLowerCase() === 'read only';
    },

    hasQuestionRichtext(question) {
      return question.question_rich_text && question.question_rich_text.length > 0;
    },

    getQuestionRichText(question) {
      return this.sanitizeHTML(question.question_rich_text);
    },

    sanitizeHTML(html) {
      return DOMPurify.sanitize(html);
    },

    isQuestionsRequired(question) {
      return question.required === 'true'
    },

    addFlexGrowClass(question) {
      if (this.isQuestionsReadOnly(question) || !this.isQuestionsRequired(question)) {
        return 'flex-grow-1';
      }
    },
  },
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css" />
<style lang="scss" scoped>
@import '../../assets/stylesheets/components/project/prqs.scss';

.input-text.box-alert,
.question-list-item.box-alert,
.form-control.box-alert {
  background-color: rgba(255, 0, 0, 0.2) !important;
  border: 1px solid #dc3545 !important;
}

::v-deep .multiselect.box-alert .multiselect__tags {
  background-color: rgba(255, 0, 0, 0.2) !important;
  border: 1px solid #dc3545 !important;
}
</style>
